import { effectiveTaxCode, marginBase, marginVat, taxCodeDefinition } from './taxCodes';
import { openAmount } from './zahlungen';
import { bwaBlocks, bwaBlockForAccount, type BwaBlockKey } from './bwaGroups';
import type { IncomingInvoice, IncomingInvoiceStatus } from './incoming/types';
import { daysOverdue, type Invoice, type InvoiceSource } from './types';

/**
 * The central finance calculation basis. Cockpit, OPOS, Auswertungen/BWA, Buchhaltung and the
 * exports all read their figures from HERE, so no two screens can disagree about a number — the
 * legacy FORQ advertises exactly this ("Rechnungen, OPOS, Auswertungen und Exporte verwenden
 * dieselbe Berechnungsbasis") and it is the one architectural promise worth keeping literally.
 * Pure functions over the two gateways' records: no fetching, no state, fully unit-testable.
 */

/** Inclusive date window. An empty bound means "unbounded on that side". */
export interface FinancePeriod {
  readonly from: string;
  readonly to: string;
}

export const unboundedPeriod: FinancePeriod = { from: '', to: '' };

export function isInPeriod(dateIso: string | null, period: FinancePeriod): boolean {
  if (!dateIso) return false;
  if (period.from && dateIso < period.from) return false;
  if (period.to && dateIso > period.to) return false;
  return true;
}

export function periodLabel(period: FinancePeriod): string {
  const format = (value: string) => new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`));
  if (!period.from && !period.to) return 'Gesamter Zeitraum';
  if (period.from && period.to) return `${format(period.from)} – ${format(period.to)}`;
  return period.from ? `ab ${format(period.from)}` : `bis ${format(period.to)}`;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** The date an outgoing document is booked on (issue date; a draft falls back to its creation day). */
export function invoiceBookingDate(invoice: Invoice): string {
  return invoice.issueDate ?? invoice.createdAt.slice(0, 10);
}

/** Legally issued documents only: an RE/GS number exists and the document is not cancelled. */
export function isBookedInvoice(invoice: Invoice): boolean {
  return invoice.invoiceNumber !== null && invoice.documentStatus !== 'cancelled';
}

/** Credit notes reduce revenue — they carry positive totals and are signed here, in one place. */
export function invoiceSign(invoice: Invoice): number {
  return invoice.kind === 'credit_note' ? -1 : 1;
}

/** An incoming invoice becomes expense once it is at least pre-booked (kontiert) and not rejected. */
const unbookedIncomingStatuses: readonly IncomingInvoiceStatus[] = ['captured', 'rejected', 'cancelled'];
export function isBookedIncoming(invoice: IncomingInvoice): boolean {
  return !unbookedIncomingStatuses.includes(invoice.status);
}

/** Documents booked inside the period — the basis every evaluation below builds on. */
export function bookedInvoicesIn(invoices: readonly Invoice[], period: FinancePeriod): readonly Invoice[] {
  return invoices.filter((invoice) => isBookedInvoice(invoice) && isInPeriod(invoiceBookingDate(invoice), period));
}

export function bookedIncomingIn(incoming: readonly IncomingInvoice[], period: FinancePeriod): readonly IncomingInvoice[] {
  return incoming.filter((invoice) => isBookedIncoming(invoice) && isInPeriod(invoice.invoiceDate, period));
}

/**
 * Period slice WITHOUT a booked-status opinion — for the exports, which apply their own rule for
 * what may be handed to the Fibu. Keeps "which period" and "which documents" separate concerns.
 */
export function invoicesInPeriod(invoices: readonly Invoice[], period: FinancePeriod): readonly Invoice[] {
  return invoices.filter((invoice) => isInPeriod(invoiceBookingDate(invoice), period));
}

export function incomingInPeriod(incoming: readonly IncomingInvoice[], period: FinancePeriod): readonly IncomingInvoice[] {
  return incoming.filter((invoice) => isInPeriod(invoice.invoiceDate, period));
}

/** Still-open receivables (the OPOS debit side) — independent of the period, an open item stays open. */
export function openReceivables(invoices: readonly Invoice[]): readonly Invoice[] {
  return invoices.filter((invoice) => invoice.paymentStatus === 'open' || invoice.paymentStatus === 'partial');
}

/** Still-open payables (the OPOS credit side). */
export function openPayables(incoming: readonly IncomingInvoice[]): readonly IncomingInvoice[] {
  return incoming.filter((invoice) => !['paid', 'cancelled', 'rejected'].includes(invoice.status));
}

// openAmount lebt jetzt in zahlungen.ts und rechnet mit settledAmount statt paidAmount --
// sonst bliebe eine mit Skonto beglichene Rechnung in OPOS und Kennzahlen ewig offen.
export { openAmount } from './zahlungen';

export interface FinanceKpis {
  readonly receivablesOpen: number;
  readonly receivablesCount: number;
  readonly payablesOpen: number;
  readonly payablesCount: number;
  /** Net revenue booked in the period (credit notes deducted). */
  readonly revenue: number;
  /** Net expense booked in the period. */
  readonly expense: number;
  /**
   * Revenue minus the expense FORQ can see — the ROHERTRAG, not the operating result. Personnel
   * costs and depreciation never arrive as a supplier invoice, so they are not in here. Calling
   * this an "Ergebnis" would overstate it by the entire payroll.
   */
  readonly grossProfit: number;
}

export function computeFinanceKpis(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  period: FinancePeriod,
): FinanceKpis {
  const revenue = bookedInvoicesIn(invoices, period)
    .reduce((sum, invoice) => sum + invoice.netTotal * invoiceSign(invoice), 0);
  const expense = bookedIncomingIn(incoming, period)
    .reduce((sum, invoice) => sum + invoice.netAmount, 0);
  const receivables = openReceivables(invoices);
  const payables = openPayables(incoming);
  return {
    receivablesOpen: round2(receivables.reduce((sum, invoice) => sum + openAmount(invoice), 0)),
    receivablesCount: receivables.length,
    payablesOpen: round2(payables.reduce((sum, invoice) => sum + invoice.grossAmount, 0)),
    payablesCount: payables.length,
    revenue: round2(revenue),
    expense: round2(expense),
    grossProfit: round2(revenue - expense),
  };
}

export interface VatRateRow {
  readonly rate: number;
  readonly net: number;
  readonly tax: number;
}

/**
 * Preparatory VAT overview (USt-Voranmeldung). Deliberately NOT a legal filing: the figures are
 * derived from the booked documents so they can be checked against the Fibu/ELSTER — the legacy
 * draws the same line ("rechtsverbindliche Buchhaltung und Steuermeldungen bleiben in Navision/
 * Fibu bzw. ELSTER") and labels its own report "vorbereitend".
 */
export interface VatReport {
  /** Revenue split by VAT rate (from the invoice lines). */
  readonly revenueByRate: readonly VatRateRow[];
  /**
   * Differenzbesteuerung (§ 25a UStG), getrennt ausgewiesen: Bemessungsgrundlage ist die Summe der
   * Margen, nicht der Umsatz. Steht auf keinem Beleg, gehoert aber in die Voranmeldung.
   */
  readonly marginBaseTotal: number;
  /** Die in den Margen enthaltene Umsatzsteuer -- Teil von vatTotal. */
  readonly marginVatTotal: number;
  /** Umsatzsteuer owed on the period's outgoing documents (einschliesslich der Margensteuer). */
  readonly vatTotal: number;
  /** Input tax split by rate (from the incoming invoices). */
  readonly inputTaxByRate: readonly VatRateRow[];
  /** Vorsteuer reclaimable from the period's incoming invoices. */
  readonly inputTaxTotal: number;
  /** Zahllast: VAT owed minus input tax (negative = refund). */
  readonly payable: number;
  /** How many documents the figures were derived from — the "Datenstand" of the report. */
  readonly outgoingCount: number;
  readonly incomingCount: number;
}

function toRateRows(byRate: Map<number, { net: number; tax: number }>): readonly VatRateRow[] {
  return [...byRate.entries()]
    .map(([rate, sums]) => ({ rate, net: round2(sums.net), tax: round2(sums.tax) }))
    .sort((left, right) => right.rate - left.rate);
}

export function computeVatReport(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  period: FinancePeriod,
): VatReport {
  const outgoing = bookedInvoicesIn(invoices, period);
  const revenueByRate = new Map<number, { net: number; tax: number }>();
  // Differenzbesteuerung getrennt fuehren: Bemessungsgrundlage ist die MARGE, nicht der Umsatz --
  // wuerde man sie in den 0-%-Topf werfen, faellt die Zahllast um die Margensteuer zu niedrig aus.
  let marginBaseTotal = 0;
  let marginVatTotal = 0;
  for (const invoice of outgoing) {
    const sign = invoiceSign(invoice);
    for (const line of invoice.lines) {
      const code = effectiveTaxCode(line);
      const definition = code ? taxCodeDefinition(code) : null;
      if (definition?.marginScheme) {
        marginBaseTotal += marginBase(line) * sign;
        marginVatTotal += marginVat(line, definition.marginRate) * sign;
        continue;
      }
      const bucket = revenueByRate.get(line.vatRate) ?? { net: 0, tax: 0 };
      bucket.net += line.netAmount * sign;
      bucket.tax += line.netAmount * (line.vatRate / 100) * sign;
      revenueByRate.set(line.vatRate, bucket);
    }
  }

  const booked = bookedIncomingIn(incoming, period);
  const inputTaxByRate = new Map<number, { net: number; tax: number }>();
  for (const invoice of booked) {
    const bucket = inputTaxByRate.get(invoice.taxRate) ?? { net: 0, tax: 0 };
    bucket.net += invoice.netAmount;
    bucket.tax += invoice.taxAmount;
    inputTaxByRate.set(invoice.taxRate, bucket);
  }

  // Die Margensteuer gehoert in die Zahllast, auch wenn sie auf keinem Beleg steht.
  const vatTotal = round2(
    [...revenueByRate.values()].reduce((sum, bucket) => sum + bucket.tax, 0) + marginVatTotal,
  );
  const inputTaxTotal = round2([...inputTaxByRate.values()].reduce((sum, bucket) => sum + bucket.tax, 0));
  return {
    revenueByRate: toRateRows(revenueByRate),
    marginBaseTotal: round2(marginBaseTotal),
    marginVatTotal: round2(marginVatTotal),
    vatTotal,
    inputTaxByRate: toRateRows(inputTaxByRate),
    inputTaxTotal,
    payable: round2(vatTotal - inputTaxTotal),
    outgoingCount: outgoing.length,
    incomingCount: booked.length,
  };
}

export interface RevenueSourceRow {
  readonly source: InvoiceSource;
  readonly amount: number;
  readonly count: number;
}

export interface BwaCostBlock {
  readonly key: BwaBlockKey;
  readonly label: string;
  readonly amount: number;
  readonly count: number;
  /** The block cannot be complete from supplier invoices alone — the UI must say so. */
  readonly incompleteFromInvoices: boolean;
  readonly source: string;
}

/**
 * BWA — the cost blocks are derived from the Kontierung of the incoming invoices, so this is a
 * real BWA structure and not just Umsatz minus Wareneingang. It stops HONESTLY at the
 * Zwischenergebnis: personnel costs and depreciation never arrive as an invoice, so the
 * Betriebsergebnis cannot be computed here and is not claimed. That is what the tax advisor's
 * BWA is for.
 */
export interface BwaReport {
  /** Gross revenue before credit notes. */
  readonly revenue: number;
  /** Erlösschmälerungen — credit notes issued in the period (positive number). */
  readonly creditNotes: number;
  /** Revenue after credit notes. */
  readonly netRevenue: number;
  /** Wareneinsatz / Fremdleistungen — the block above the Rohertrag. */
  readonly materialExpense: number;
  /** Rohertrag — net revenue minus Wareneinsatz. */
  readonly grossProfit: number;
  /** Rohertragsquote in percent (0 when there is no revenue). */
  readonly grossProfitRate: number;
  /** Every cost block found in the Kontierung, Wareneinsatz included. */
  readonly costBlocks: readonly BwaCostBlock[];
  /** The cost blocks BELOW the Rohertrag, as far as FORQ can see them. */
  readonly operatingCosts: number;
  /** Rohertrag minus those costs. Explicitly NOT the Betriebsergebnis. */
  readonly interimResult: number;
  /** Booked expense whose account is not part of the operating result (or is unknown). */
  readonly unassignedExpense: number;
  readonly revenueBySource: readonly RevenueSourceRow[];
}

export function computeBwaReport(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  period: FinancePeriod,
): BwaReport {
  const booked = bookedInvoicesIn(invoices, period);
  const revenue = round2(booked
    .filter((invoice) => invoice.kind === 'invoice')
    .reduce((sum, invoice) => sum + invoice.netTotal, 0));
  const creditNotes = round2(booked
    .filter((invoice) => invoice.kind === 'credit_note')
    .reduce((sum, invoice) => sum + invoice.netTotal, 0));
  const netRevenue = round2(revenue - creditNotes);

  // Cost blocks come from the Kontierung — the account decides, not a guess.
  const bookedIncoming = bookedIncomingIn(incoming, period);
  const blockSums = new Map<BwaBlockKey, { amount: number; count: number }>();
  let unassignedExpense = 0;
  for (const invoice of bookedIncoming) {
    const key = invoice.accountNumber ? bwaBlockForAccount(invoice.accountNumber) : null;
    if (!key) {
      unassignedExpense += invoice.netAmount;
      continue;
    }
    const bucket = blockSums.get(key) ?? { amount: 0, count: 0 };
    bucket.amount += invoice.netAmount;
    bucket.count += 1;
    blockSums.set(key, bucket);
  }

  const costBlocks: BwaCostBlock[] = bwaBlocks
    .filter((block) => blockSums.has(block.key) || block.incompleteFromInvoices)
    .map((block) => {
      const bucket = blockSums.get(block.key) ?? { amount: 0, count: 0 };
      return {
        key: block.key,
        label: block.label,
        amount: round2(bucket.amount),
        count: bucket.count,
        incompleteFromInvoices: block.incompleteFromInvoices,
        source: block.source,
      };
    });

  const materialExpense = round2(blockSums.get('material')?.amount ?? 0);
  const grossProfit = round2(netRevenue - materialExpense);
  const operatingCosts = round2(costBlocks
    .filter((block) => block.key !== 'material')
    .reduce((sum, block) => sum + block.amount, 0));

  const bySource = new Map<InvoiceSource, { amount: number; count: number }>();
  for (const invoice of booked) {
    const bucket = bySource.get(invoice.sourceType) ?? { amount: 0, count: 0 };
    bucket.amount += invoice.netTotal * invoiceSign(invoice);
    bucket.count += 1;
    bySource.set(invoice.sourceType, bucket);
  }

  return {
    revenue,
    creditNotes,
    netRevenue,
    materialExpense,
    grossProfit,
    grossProfitRate: netRevenue === 0 ? 0 : round2((grossProfit / netRevenue) * 100),
    costBlocks,
    operatingCosts,
    interimResult: round2(grossProfit - operatingCosts),
    unassignedExpense: round2(unassignedExpense),
    revenueBySource: [...bySource.entries()]
      .map(([source, bucket]) => ({ source, amount: round2(bucket.amount), count: bucket.count }))
      .sort((left, right) => right.amount - left.amount),
  };
}

export type AgingBucketKey = 'not_due' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus';

export interface AgingBucket {
  readonly key: AgingBucketKey;
  readonly label: string;
  readonly amount: number;
  readonly count: number;
}

const agingOrder: readonly { key: AgingBucketKey; label: string }[] = [
  { key: 'not_due', label: 'Nicht fällig' },
  { key: 'd1_30', label: '1–30 Tage' },
  { key: 'd31_60', label: '31–60 Tage' },
  { key: 'd61_90', label: '61–90 Tage' },
  { key: 'd90_plus', label: 'Über 90 Tage' },
];

export function agingBucketFor(days: number): AgingBucketKey {
  if (days <= 0) return 'not_due';
  if (days <= 30) return 'd1_30';
  if (days <= 60) return 'd31_60';
  if (days <= 90) return 'd61_90';
  return 'd90_plus';
}

/** Receivables aged into the classic 30/60/90 buckets — the OPOS view the Leitung asks for. */
export function computeReceivablesAging(invoices: readonly Invoice[], todayIsoDate: string): readonly AgingBucket[] {
  const sums = new Map<AgingBucketKey, { amount: number; count: number }>();
  for (const invoice of openReceivables(invoices)) {
    const key = agingBucketFor(daysOverdue(invoice, todayIsoDate));
    const bucket = sums.get(key) ?? { amount: 0, count: 0 };
    bucket.amount += openAmount(invoice);
    bucket.count += 1;
    sums.set(key, bucket);
  }
  return agingOrder.map(({ key, label }) => {
    const bucket = sums.get(key) ?? { amount: 0, count: 0 };
    return { key, label, amount: round2(bucket.amount), count: bucket.count };
  });
}
